import React from 'react'
import type { ComponentConfig, Slot } from '@puckeditor/core'
import { Spacing, defaultSpacing, SpacingGrid } from '../../fields/SpacingField'
import { withStyleFields } from '../../components/StyleWrapper'
import {
  BackgroundStyleProps,
  BorderStyleProps,
  applyStyleProps,
} from '../../fields/StyleFields'

export type PositionerProps = {
  mode: 'relative' | 'absolute'
  margins: Spacing
  offsets: Spacing
  width?: string
  height?: string
  transform?: string
  zIndex?: number
  items: Slot
  background?: BackgroundStyleProps
  border?: BorderStyleProps
}

const allFields: ComponentConfig<PositionerProps>['fields'] = {
  mode: {
    label: 'Positioning Mode',
    type: 'radio',
    options: [
      { label: 'Relative', value: 'relative' },
      { label: 'Absolute', value: 'absolute' },
    ],
  },
  margins: {
    label: 'External Margins (px)',
    type: 'custom',
    render: ({ value, onChange }) => (
      <SpacingGrid
        title="External Margins (px)"
        value={value ?? defaultSpacing}
        onChange={onChange}
      />
    ),
  },
  offsets: {
    label: 'Offset Coordinates (px)',
    type: 'custom',
    render: ({ value, onChange }) => (
      <SpacingGrid
        title="Offset Coordinates (px)"
        value={value ?? defaultSpacing}
        onChange={onChange}
      />
    ),
  },
  width: {
    label: 'Width',
    type: 'text',
    placeholder: 'e.g. 200px, 50%',
  },
  height: {
    label: 'Height',
    type: 'text',
    placeholder: 'e.g. 100px, auto',
  },
  transform: {
    label: 'Transform',
    type: 'text',
    placeholder: 'e.g. translate(-50%, -50%)',
  },
  zIndex: {
    label: 'Z-Index',
    type: 'number',
  },
  items: {
    type: 'slot',
  },
}

const relativeFields: ComponentConfig<PositionerProps>['fields'] = {
  mode: allFields.mode,
  margins: allFields.margins,
  width: allFields.width,
  height: allFields.height,
  transform: allFields.transform,
  zIndex: allFields.zIndex,
  items: allFields.items,
} as ComponentConfig<PositionerProps>['fields']

export const Positioner = withStyleFields<ComponentConfig<PositionerProps>>({
  // What the editor's agent reads about this block (AgentComponentNotes).
  ai: {
    description:
      'Wrapper that nudges its children (relative) or lays them over the parent (absolute, e.g. a badge on an image).',
    fields: {
      margins: 'Object {top, right, bottom, left} of px values as strings.',
      offsets:
        'Absolute mode only: {top, right, bottom, left}; an empty side stays auto.',
    },
  },
  fields: allFields,
  defaultProps: {
    mode: 'relative',
    margins: defaultSpacing,
    offsets: defaultSpacing,
    items: [],
  },
  resolveFields: (data) => {
    if (data.props.mode === 'absolute') {
      return allFields
    }
    return relativeFields
  },
  render: ({
    mode,
    margins,
    offsets,
    width,
    height,
    transform,
    zIndex,
    background,
    border,
    items: Items,
  }) => {
    const isAbsolute = mode === 'absolute'
    const v = (s: string | undefined) => {
      if (!s || s === '0') return undefined
      if (/^\d+(\.\d+)?$/.test(s)) return `${s}px`
      return s
    }
    // Offsets differ from `v`: only skip when truly empty (so '0' is honored,
    // and an unset side stays `auto` — required to center with left:50%).
    const off = (s: string | undefined) => {
      if (s == null || s === '') return undefined
      if (/^\d+(\.\d+)?$/.test(s)) return `${s}px`
      return s
    }
    return (
      <div
        style={{
          position: isAbsolute ? 'absolute' : 'relative',
          marginTop: v(margins?.top),
          marginBottom: v(margins?.bottom),
          marginLeft: v(margins?.left),
          marginRight: v(margins?.right),
          width: v(width),
          height: v(height),
          transform: transform || undefined,
          zIndex: zIndex,
          ...(isAbsolute && {
            top: off(offsets?.top),
            left: off(offsets?.left),
            right: off(offsets?.right),
            bottom: off(offsets?.bottom),
          }),
          ...applyStyleProps({ background, border }),
        }}
      >
        <Items className="no-slot-padding" />
      </div>
    )
  },
})
